import { PaymentMethodType, Money } from '@afterpay/types'

export type LogEvent = (eventType: string, eventProperties?: object) => void

export enum PageNames {
  ADD_MOBILE = 'Add Mobile',
  ADD_MOBILE_VERIFY = 'Add mobile verify',
  ADD_NEW_SHIPPING_ADDRESS = 'Add New Shipping Address',
  ADJUST_AMOUNT = 'Adjust Amount',
  APPLE_PAYLATER_ADD_CARD = 'Add Card',
  APPLE_PAYLATER_INITIALISE = 'Initialise',
  APPLE_PAYLATER_INITIAL_LOADING = 'Apple Pay Later Initial Loading',
  APPLE_PAYLATER_LOGIN_EMAIL = 'Apple Pay Later Login Email',
  APPLE_PAYLATER_LOGIN_PASSWORD = 'Apple Pay Later Login Password',
  APPLE_PAYLATER_LOGGED_IN = 'Apple Pay Later Logged In',
  APPLE_PAYLATER_SUMMARY = 'Apple Pay Later Summary',
  EDIT_SHIPPING_ADDRESS = 'Edit Shipping Address',
  ADD_NEW_PAYMENT_METHOD = 'Add New Payment Method',
  ADD_PAYMENT_METHOD = 'Add First Payment Method',
  CASH_AUTH = 'Cash Authentication',
  CASH_AUTH_INLINE = 'Cash Authentication (Inline)',
  CASH_APP_PAY_REMEDIATION = 'Cash App Pay Remediation',
  CASH_APP_PAY_UNAVAILABLE = 'Cash App Pay Unavailable',
  CARD_EDUCATION = 'Card Education',
  CONSUMER_LENDING_SUMMARY = 'Consumer Lending Summary',
  CREATE_PASSWORD = 'Create Password',
  CREATE_ACCOUNT = 'Create Account',
  DOB_LAST_NAME_VERIFY = 'DOB Last Name Verify Page',
  EXISTING_MOBILE_NUMBER = 'Existing Mobile Number',
  FIRST_RETURN_VERIFY = 'First Return Verify',
  INDEX = 'Index',
  INITIAL_LOADING = 'Initial Loading',
  LOGIN_EMAIL = 'Login Email',
  LOGIN_PASSWORD = 'Login Password',
  PASSWORD_RESET_REQUIRED = 'Password Reset Required',
  NEW_DEVICE_VERIFY = 'New Device Verify',
  ONE_TIME_PASSWORD = 'Login One Time Code',
  PAY = 'Pay',
  PAYMENT_OPTIONS = 'Payment Options',
  PAYMENT_METHOD = 'Payment Method',
  PAYMENT_TYPES = 'Payment Types',
  PREFERRED_PAYMENT_DAY = 'Preferred Payment Day',
  LOGGED_IN = 'Logged In',
  REGISTER_VERIFY = 'Register Verify',
  RESET_VERIFY = 'Reset Verify',
  CHECKOUT_CONFIRM_VERIFY = 'Checkout Confirm Verify',
  EFA_VERIFY = 'EFA Verify',
  SHIPPING_ADDRESS = 'Shipping Address',
  SIGN_UP = 'Sign Up',
  SIGNUP_RETRY = 'Signup Retry',
  SIGNUP_RETRY_ADDRESS = 'Signup Retry Address',
  SUMMARY = 'Summary',
  TERMINAL_ERROR = 'Terminal Error',
  TRUTH_IN_LENDING = 'Truth In Lending',
  UNSUPPORTED_BROWSER = 'Unsupported Browser',
  VERIFY = 'Verify',
  VERIFY_CHALLENGE = 'Verify Challenge',
  VERIFY_IDENTITY = 'HardID Landing',
  VERIFYING_BIOMETRIC = 'Verify Identity Result Loading',
  VERIFY_LICENCE = 'HardID Drivers Licence',
  VERIFY_PASSPORT = 'HardID Passport',
  VERIFY_MEDICARE = 'HardID Medicare',
  RETRY_VERIFY_HARD_ID = 'HardID Retry',
  VERIFY_IDENTITY_HARD_ID = 'HardID Collection Required',
  VERIFY_IDENTITY_START = 'Verify Identity Start',
  CARD_SCAN = 'Card Scan',
  LIMIT_EXCEEDED = 'Limit Exceeded',
  NOT_FOUND = '404',
  PAY_MONTHLY = 'Pay Monthly',
  PAY_MONTHLY_ONLY = 'Pay Monthly Only',
  RETRY_CREDIT_FREEZE = 'Retry Credit Freeze',
  OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE = 'Overdue Payments',
  KYC_CONFIRM_DETAILS = 'Re-KYC - Confirm Your Details',
  KYC_UPDATE_ADDRESS = 'Re-KYC - Update Address',
  UNSUITABILITY_QUESTIONS = 'Unsuitability Questions - How Afterpay Works',
  AFFORDABILITY_QUESTIONS = 'Affordability Questions',
  CONTRACT_AGREEMENT = 'Contract Agreement',
  VERIFY_BIOMETRIC_PERSONA = 'Verify Biometric Persona',
  EDUCATION_INTRO = 'NUX',
  GET_STARTED = 'Get Started',
  PROFILE_CREATED = 'Profile Created',
  WELCOME_WITH_INITIAL_LIMIT = 'Welcome With Initial Limit',
}

export const viewedPageEventName = (pageName: PageNames): string => `Viewed ${pageName} Page`
export const getEnteredPrefillEvent = (attr: string): string => `Entered ${PageNames.SIGN_UP} Page ${attr} Field`
export const pressedPageReturnEventName = (pageName: PageNames): string => `Pressed ${pageName} Page Return`

export const TrackingEvent = {
  ADDRESS_ERROR: 'Viewed Invalid Address Error',
  AUTHENTICATION_FAILURE_LOCKOUT: 'Authentication Failure Lockout',
  AUTHENTICATION_FAILURE: 'Authentication Failure',
  BIOMETRIC_LOGIN_LOCAL_STORAGE_PRESENT: 'Biometric Login Local Storage Present',
  BIOMETRIC_LOGIN_ELIGIBILITY: 'Biometric Login Eligibility',
  SOFT_ID_CHECK_FAILED: 'Soft ID Check Failed',
  CHECKOUT_LOGIN_PAYWATCH_FAILED: 'Checkout Login Paywatch Failed',
  CHANGED_ADD_NEW_PAYMENT_METHOD_BILLING_ADDRESS: `Changed ${PageNames.ADD_NEW_PAYMENT_METHOD} Billing Address`,
  CHANGED_PREFILLED_EMAIL_OR_MOBILE: 'Changed prefilled email or mobile',
  CHANGED_SHIPPING_COUNTRY: 'Changed Shipping Country',
  CHECKOUT_APPLE_PAY_ERROR: 'Checkout Apple Pay Error',
  CHECKOUT_EXCHANGED_APPLE_PAY_TOKEN: 'Checkout Exchanged Apple Pay Token',
  CHECKOUT_BIOMETRIC_LOGIN_FAILED: 'Checkout Biometric Login failed',
  CHECKOUT_BIOMETRIC_LOGIN_SUCCESSFUL: 'Checkout Biometric Login successful',
  CHECKOUT_DECLINED: 'Checkout Declined',
  CHECKOUT_CONFIRM_DISABLED: 'Checkout Confirm Button Disabled',
  CHECKOUT_FATAL_ERROR: 'Checkout Fatal Error',
  CHECKOUT_INITIATED: 'Checkout Initiated',
  CHECKOUT_TOPAZ_ERROR: 'Checkout Topaz Error',
  CLOSE_CALLED: 'Checkout Close Called',
  CLOSED_PAY_MONTHLY_ONLY_PBI_MODAL: 'Close Pay Monthly Only PBI Modal',
  DECLINED_ALL_BIOMETRIC_OPT_INS: 'Declined All Biometric Opt-Ins',
  DOB_OVER_ERROR: 'Overage DOB Error',
  DOB_UNDER_ERROR: 'Underage DOB Error',
  DRIVER_LICENCE_SUBMIT_FAILED: 'Driver Licence IDV Submit Failed',
  ENABLED_BIOMETRIC_LOGIN_SUCCESSFUL: 'Enable Biometric Login Successful',
  ENABLED_BIOMETRIC_LOGIN_FAILED: 'Enable Biometric Login Failed',
  ERROR_ON_SAVE_SHIPPING_ADDRESS: 'Error On Save Shipping Address',
  INDEX_PAGE_LOADED: `${PageNames.INDEX} page loaded`,
  APPLE_PAYLATER_INDEX_PAGE_LOADED: 'Apple Pay Later Index page loaded',
  INVALID_PICKUP_ADDRESS: 'Invalid Pickup Address Error',
  LOGIN_ATTEMPTED: 'Login Attempted',
  LOGIN_WEB_PASSWORD: 'Web Password',
  MEDICARE_SUBMIT_FAILED: 'Medicare IDV Submit Failed',
  MERCHANT_SHIPPING_OPTION_RETRY: 'Merchant Shipping Options Retry',
  MERCHANT_SHIPPING_OPTIONS_BAD_RESPONSE: 'Merchant Shipping Options Bad Response',
  MERCHANT_SHIPPING_OPTIONS_SERVICE_UNAVAILABLE: 'Merchant Shipping Options Service Unavailable',
  MERCHANT_SHIPPING_OPTIONS_TIMEOUT: 'Merchant Shipping Options Timeout',
  MERCHANT_SHIPPING_OPTIONS_TOTAL_AMOUNT_TOO_HIGH: 'Merchant Shipping Options Total Amount Too High',
  MERCHANT_UNRECOGNIZED_SHIPPING_ADDRESS: 'Merchant Unrecognized Shipping Address',
  MERCHANT_UNSUPPORTED_SHIPPING_ADDRESS: 'Merchant Unsupported Shipping Address',
  MOBILE_NUMBER_ERROR: 'Invalid Mobile Number Error',
  NAME_ERROR: 'Invalid Name Error',
  NEW_CARD_ADDED_3DS_FAILED: 'New Card Added 3DS Failed',
  NEW_CARD_ADDED_3DS_PASSED: 'New Card Added 3DS Passed',
  PAID_OVERDUE_PAYMENT: 'Paid Overdue Payment',
  PAID_OUTSTANDING_PAYMENT: 'Paid Outstanding Payment',
  PASSPORT_SUBMIT_FAILED: 'Passport IDV Submit Failed',
  PAY_PAGE_LOADED: `${PageNames.PAY} page loaded`,
  POST_MESSAGE_ERROR: 'Post Message Error',
  POST_MESSAGE_RECEIVED: 'Post Message Received',
  POST_MESSAGE_SENT: 'Post Message Sent',
  POST_MESSAGE_UNAVAILABLE: 'Post Message Unavailable',
  PRESSED_ADD_FIRST_PAYMENT_CONTINUE: `Pressed ${PageNames.ADD_PAYMENT_METHOD} Page Continue`,
  PRESSED_ADD_NEW_PAYMENT_METHOD_RETURN: `Pressed ${PageNames.ADD_NEW_PAYMENT_METHOD} Page Return`,
  PRESSED_ADD_NEW_PAYMENT_METHOD_SAVE_BUTTON: `Pressed ${PageNames.ADD_NEW_PAYMENT_METHOD} Page Save Button`,
  PRESSED_ADD_NEW_SHIPPING_ADDRESS_RETURN: `Pressed ${PageNames.ADD_NEW_SHIPPING_ADDRESS} Page Return`,
  PRESSED_ADD_NEW_SHIPPING_ADDRESS: `Pressed ${PageNames.ADD_NEW_SHIPPING_ADDRESS}`,
  PRESSED_ADJUST_AMOUNT_PAGE_CONTINUE_BUTTON: `Pressed ${PageNames.ADJUST_AMOUNT} Page Continue Button`,
  PRESSED_BACK_TO_STORE: 'Pressed Back To Store',
  PRESSED_BIOMETRIC_OPT_IN_MODAL_TURN_ON: 'Pressed Biometric Opt-In Modal Turn On',
  PRESSED_BIOMETRIC_OPT_IN_MODAL_MAYBE_LATER: 'Pressed Biometric Opt-In Modal Maybe Later',
  PRESSED_BIOMETRIC_OPT_IN_MODAL_CLOSE: 'Pressed Biometric Opt-In Modal Close',
  PRESSED_CHANGE_SHIPPING_ADDRESS_RETURN: `Pressed Change ${PageNames.SHIPPING_ADDRESS} Page Return`,
  PRESSED_CHANGE_SHIPPING_ADDRESS: `Pressed Change ${PageNames.SHIPPING_ADDRESS}`,
  PRESSED_CLOSE_BROWSER_TAB: 'Pressed Close on Browser Tab',
  PRESSED_CREATE_ACCOUNT_PAGE_EDIT: `Pressed ${PageNames.CREATE_ACCOUNT} Page Edit`,
  PRESSED_DOB_LAST_NAME_VERIFY_PAGE_CONTINUE: 'Pressed DOB Last Name Verify Page Continue',
  PRESSED_EDIT_SHIPPING_ADDRESS: `Pressed Edit ${PageNames.SHIPPING_ADDRESS}`,
  PRESSED_ENTER_ADDRESS_MANUALLY: 'Pressed Enter Address Manually',
  PRESSED_EFA_ROBOCALL_RETURN_TO_MERCHANT_BUTTON: 'Pressed EFA Robocall Return to Merchant button',
  PRESSED_FORGOT_PASSWORD: 'Pressed Forgot Password',
  PRESSED_HARDID_DRIVERS_LICENCE_PAGE_VERIFY_BUTTON: 'Pressed HardID Drivers Licence Page Verify Button',
  PRESSED_HARDID_LANDING_PAGE_DRIVERS_LICENCE_BUTTON: 'Pressed HardID Landing Page Licence Button',
  PRESSED_HARDID_LANDING_PAGE_MEDICARE_BUTTON: 'Pressed HardID Landing Page Medicare Button',
  PRESSED_HARDID_LANDING_PAGE_PASSPORT_BUTTON: 'Pressed HardID Landing Page Passport Button',
  PRESSED_SOFTID_LANDING_PAGE_ADDRESS_VERIFY_BUTTON: 'Pressed SoftID Landing Page Address Verify Button',
  PRESSED_HARDID_LANDING_PAGE_HELP_LINK: 'Pressed HardID Landing Page Help Link',
  PRESSED_HARDID_MAX_FAILED_HELP_BUTTON: 'Pressed HardID Max Failure Help Button',
  PRESSED_HARDID_MEDICARE_PAGE_VERIFY_BUTTON: 'Pressed HardID Medicare Page Verify Button',
  PRESSED_HARDID_PASSPORT_PAGE_VERIFY_BUTTON: 'Pressed HardID Passport Page Verify Button',
  PRESSED_HARDID_RETRY_PAGE_VERIFY_BUTTON: 'Pressed HardID Retry Page Verify Button',
  PRESSED_HARDID_RETRY_PAGE_HELP_LINK: 'Pressed HardID Retry Page Help Link',
  PRESSED_LEAVE_CHECKOUT_MODAL_CANCEL: 'Pressed Leave Checkout Modal Cancel',
  PRESSED_LEAVE_CHECKOUT_MODAL_LEAVE: 'Pressed Leave Checkout Modal Leave',
  PRESSED_SUMMARY_LEAVE_CHECKOUT_MODAL_CANCEL: 'Pressed Summary Leave Checkout Modal Cancel',
  PRESSED_SUMMARY_LEAVE_CHECKOUT_MODAL_LEAVE: 'Pressed Summary Leave Checkout Modal Leave',
  PRESSED_SUMMARY_LEAVE_CHECKOUT_MODAL_STAY: 'Pressed Summary Leave Checkout Modal Stay',
  PRESSED_LOGIN_EMAIL_CONTINUE: `Pressed ${PageNames.LOGIN_EMAIL} Page Continue`,
  PRESSED_LOGIN_PASSWORD_CONTINUE: `Pressed ${PageNames.LOGIN_PASSWORD} Page Continue`,
  PRESSED_LOGIN_PASSWORD_PAGE_BIOMETRIC_LOGIN: `Pressed ${PageNames.LOGIN_PASSWORD} Page Biometric Login`,
  PRESSED_EXPLICIT_LOGIN_PASSWORD_PAGE_BIOMETRIC_LOGIN: `Pressed ${PageNames.LOGIN_PASSWORD} Page Explicit Biometric Login Button`,
  PRESSED_PAYMENT_METHOD_NEW_CARD: `Pressed ${PageNames.PAYMENT_METHOD} Page New Card`,
  PRESSED_PAYMENT_METHOD_RETURN: `Pressed ${PageNames.PAYMENT_METHOD} Page Return`,
  PRESSED_PAYMENT_METHOD_SAVE_BUTTON: `Pressed ${PageNames.PAYMENT_METHOD} Page Save Button`,
  PRESSED_READ_MORE_LINK: 'Pressed Read More Link',
  PRESSED_SAVE_NEW_SHIPPING_ADDRESS: 'Pressed Save New Shipping Address Button',
  PRESSED_SCAN_PAYMENT: 'Scan Payment Clicked',
  PRESSED_SEARCH_FOR_ADDRESS: 'Pressed Search For Address',
  PRESSED_SHIPPING_ADDRESS_SAVE_BUTTON: `Pressed ${PageNames.SHIPPING_ADDRESS} Page Save Button`,
  PRESSED_SMS_TERMS: 'Pressed SMS Terms',
  PRESSED_SUMMARY_CHANGE_PAYMENT_METHOD: `Pressed ${PageNames.SUMMARY} Page Change Payment Method`,
  PRESSED_SUMMARY_CONFIRM_BUTTON: `Pressed ${PageNames.SUMMARY} Confirm Button`,
  PRESSED_SUMMARY_INSTALMENT_AGREEMENT_LINK: `Pressed ${PageNames.SUMMARY} Page Instalment Agreement Link`,
  PRESSED_SUMMARY_PAYMENT_AUTHORISATION_LINK: `Pressed ${PageNames.SUMMARY} Page Payment Authorisation Link`,
  PRESSED_SUMMARY_PAGE_CONFIRM_BUTTON_DISABLED: `Pressed ${PageNames.SUMMARY} Page Confirm Button When Disabled`,
  PRESSED_SUMMARY_PAGE_TERMS_LINK: `Pressed ${PageNames.SUMMARY} Page Terms Link`,
  PRESSED_SUMMARY_PAGE_PRIVACY_POLICY_LINK: `Pressed ${PageNames.SUMMARY} Page Privacy Policy Link`,
  PRESSED_SUMMARY_SECTION: `Pressed ${PageNames.SUMMARY} Section`,
  PRESSED_SUMMARY_SECTION_ACTION: `Pressed ${PageNames.SUMMARY} Section Action`,
  PRESSED_TERMINAL_ERROR_LINK: 'Pressed Terminal Error Link',
  PRESSED_VERIFY_EMAIL_BUTTON: `Pressed ${PageNames.VERIFY} Page Email Button`,
  PRESSED_VERIFY_SMS_BUTTON: `Pressed ${PageNames.VERIFY} Page SMS Button`,
  PRESSED_LIMIT_EXCEEDED_CONFIRM_BUTTON: `Pressed ${PageNames.LIMIT_EXCEEDED} Confirm Button`,
  PRESSED_CONTRACT_AGREEMENT_PAGE_CONTINUE_BUTTON: `Pressed ${PageNames.CONTRACT_AGREEMENT} Page Continue Button`,
  PRESSED_CONTRACT_AGREEMENT_PAGE_CREDIT_CONTRACT_LINK: `Pressed ${PageNames.CONTRACT_AGREEMENT} Page General Terms Link`,
  PRESSED_CONTRACT_AGREEMENT_PAGE_STATEMENTS_LINK: `Pressed ${PageNames.CONTRACT_AGREEMENT} Page Credit Contract Link`,
  PRESSED_GET_STARTED_CONTINUE_BUTTON: `Pressed ${PageNames.GET_STARTED} Continue Button`,
  PRESSED_PAY_MONTHLY_ONLY_LEARN_MORE_LINK: 'Pressed Learn More Link on Pay Monthly Only Page',
  REJECTED_OVERDUE_PAYMENT: 'Rejected Overdue Payment - ',
  SCAN_PAYMENT_FAILED: 'Scan Payment Failed',
  SCAN_PAYMENT_MATCHED: 'Scan Payment Matched',
  SELECTED_SHIPPING_ADDRESS: `Selected ${PageNames.SHIPPING_ADDRESS}`,
  SELECTED_SHIPPING_OPTION: 'Selected Shipping Option',
  SELECTED_PAYMENT_METHOD: 'Selected Payment Method',
  TOGGLE_ADD_NEW_PAYMENT_METHOD_CARD_ADDRESS: `Toggle ${PageNames.ADD_NEW_PAYMENT_METHOD} Card Address`,
  TOGGLE_MANUAL_ADDRESS: `Toggle ${PageNames.SIGN_UP} Page Manual Address`,
  TOGGLE_REMEMBER_ME: 'Toggle Remember Me',
  TOGGLE_REVEAL_PASSWORD: `Toggle Reveal Password Field On ${PageNames.LOGIN_PASSWORD} Page`,
  TOGGLED_DONATION_BUTTON: 'Toggled Donation Button',
  TOGGLED_SUMMARY_PAGE_SCHEDULE: 'Toggled Summary Page Payment Schedule',
  TOGGLED_ORDER_ITEMS: 'Toggled Order Items',
  TOGGLED_PREFERRED_ADDRESS: 'Toggled Preferred Address',
  TOGGLED_MARKETING_OPT_IN: 'Toggled Marketing Opt-In',
  TOGGLED_SHIPPING_OPTIONS: 'Toggled Shipping Options',
  TRIGGER_PREFILL_STATUS: `Trigger ${PageNames.SIGN_UP} Page Prefill Status`,
  TRIGGER_UNVERIFIED_MOBILE: 'Trigger Unverified Mobile',
  VIEWED_ADJUSTED_DOWN_PAYMENT_MESSAGING: 'Viewed Adjusted Down Payment Messaging',
  VIEWED_3DS_VERIFICATION_INITIATE_FAILED: 'Viewed 3DS verification Initiate failed',
  VIEWED_3DS_VERIFICATION_INITIATE: 'Viewed 3DS verification Initiate',
  VIEWED_ANONYMOUS_USER_LOGIN_ERROR: 'Viewed Anonymous User Login Error',
  VIEWED_BIOMETRIC_OPT_IN_MODAL: 'Viewed Biometric Opt-In Modal',
  VIEWED_CARD_SCAN_PAGE: `Viewed ${PageNames.CARD_SCAN}`,
  VIEWED_CHECKOUT_TIMEOUT: `Viewed Checkout Timeout`,
  VIEWED_CHECKOUT_SUMMARY_TERMS: `Viewed Checkout ${PageNames.SUMMARY} Terms`,
  VIEWED_DEFAULT_PAYMENT_METHOD: `Viewed Default ${PageNames.PAYMENT_METHOD}`,
  VIEWED_DONATION: 'Viewed Donation',
  VIEWED_EXISTING_EMAIL_ERROR: 'Viewed Existing Email Error',
  VIEWED_EXISTING_MOBILE_NUMBER_ERROR: `Viewed ${PageNames.EXISTING_MOBILE_NUMBER} Error`,
  VIEWED_INCOMPLETE_SHIPPING_ADDRESS_ERROR: `Viewed Incomplete ${PageNames.SHIPPING_ADDRESS} Error`,
  VIEWED_LEAVE_CHECKOUT_MODAL: 'Viewed Leave Checkout Modal',
  VIEWED_SUMMARY_LEAVE_CHECKOUT_MODAL: 'Viewed Summary Leave Checkout Modal',
  VIEWED_NO_PICKUP_ADDRESS_ERROR: 'Viewed No Pickup Address Error',
  VIEWED_NON_TERMINAL_ERROR: 'Viewed Non-terminal error',
  VIEWED_ONE_TIME_PASSWORD_FAIL_TOAST: `Viewed ${PageNames.ONE_TIME_PASSWORD} Page Fail Toast`,
  VIEWED_ONE_TIME_PASSWORD_SENT_TOAST: `Viewed ${PageNames.ONE_TIME_PASSWORD} Page Sent Toast`,
  VIEWED_OVERDUE_PAYMENT_DRAWER: 'Viewed Overdue Payment Drawer',
  VIEWED_OVERDUE_LACK_OF_ELIGIBLE_PAYMENT_METHOD_ERROR: 'Viewed Overdue Lack of Eligible Payment Method Error',
  VIEWED_PROCESSING_MODAL: 'Viewed Processing Modal',
  VIEWED_LOADING_ONLY_MODAL: 'Viewed Loading Only Modal',
  VIEWED_PROMO: 'Viewed Promo',
  VIEWED_PROMO_CODE_INPUT: 'Viewed Promo Code Input',
  PRESSED_PROMO_CODE_TOGGLE: 'Pressed Promo Code Toggle',
  PRESSED_PROMO_CODE_APPLY: 'Pressed Promo Code Apply',
  PROMO_CODE_APPLIED: 'Promo Code Applied',
  PROMO_CODE_APPLY_FAILED: 'Promo Code Apply Failed',
  VIEWED_SHIPPING_ADDRESS_PAGE: `Viewed Add New ${PageNames.SHIPPING_ADDRESS} Page`,
  VIEWED_SOME_SHIPPING_OPTIONS_OVER_LIMIT: 'Viewed Some Shipping Options Over Limit',
  VIEWED_TERMINAL_ERROR_PAGE_NEED_HELP: 'Viewed Terminal Error Page Need Help',
  VIEWED_UNSUPPORTED_BROWSER_PAGE: 'Viewed Unsupported Browser Page',
  VIEWED_VERIFY_FAIL_TOAST: `Viewed ${PageNames.VERIFY} Page Fail Toast`,
  VIEWED_VERIFY_INVALID_CODE: `Viewed ${PageNames.VERIFY} Page Invalid Code`,
  VIEWED_VERIFY_ERROR: `Viewed ${PageNames.VERIFY} Page Error`,
  VIEWED_VERIFY_SENT_TOAST: `Viewed ${PageNames.VERIFY} Page Sent Toast`,
  ZOID_MESSAGE_ERROR: 'Zoid Message Error',
  ZOID_MESSAGE_RECEIVED: 'Zoid Message Received',
  ZOID_MESSAGE_SENT: 'Zoid Message Sent',
  ZOID_UNAVAILABLE: 'Zoid Unavailable',

  VIEWED_CL_IDV_PAGE: 'Viewed CL IDV Page',
  VIEWED_CL_LOADING_PAYMENT_OPTIONS_PAGE: 'Viewed CL Loading Payment Options Page',
  VIEWED_CL_PAYMENT_OPTIONS_PAGE: 'Viewed CL Payment Options Page',
  VIEWED_SUMMARY_PAGE: 'Viewed Summary Page',
  VIEWED_CL_EDIT_PLAN_PAGE: 'Viewed CL Edit Plan Page',
  VIEWED_CL_EFA_ROBOCALL_MODAL: 'Viewed CL efa robocall modal',
  VIEWED_CL_RETRY_CREDIT_FREEZE_PAGE: 'Viewed CL Retry Credit Freeze Page',
  VIEWED_CL_TRUTH_IN_LENDING_PAGE: 'Viewed CL Truth In Lending Page',
  VIEWED_CL_CREDIT_FREEZE_TOO_MANY_REQUESTS_TOAST: 'Viewed CL Credit Freeze Too Many Requests Toast',

  PRESSED_CL_ENTRY_PAGE_CONTINUE_BUTTON: 'Pressed CL Entry Page Continue Button',
  PRESSED_CL_PLAN_PAGE_CONTINUE_BUTTON: 'Pressed CL Plan Page Continue Button',
  PRESSED_CL_CREDIT_FREEZE_RETRY_BUTTON: 'Pressed CL Credit Freeze Retry Button',
  PRESSED_CL_CREDIT_FREEZE_CONTINUE_WITH_PAY_IN_4_BUTTON: 'Pressed CL Credit Freeze Continue With Pay in 4 button',
  PRESSED_SUMMARY_PAGE_CONTINUE_BUTTON: 'Pressed Summary Page Continue Button',
  PRESSED_VERIFY_IDENTITY_BUTTON: 'Pressed Verify Identity Button',

  TOGGLED_CL_AUTO_PAY_TOGGLE: 'Toggled CL Auto Pay toggle',
  TOGGLED_CL_PAYMENT_SCHEDULE: 'Toggled CL Payment Schedule',
  PRESSED_CL_PLAN_EDIT_BUTTON: 'Pressed CL Plan Edit Button',
  PRESSED_CL_EDIT_PLAN_PAGE_CONTINUE_BUTTON: 'Pressed CL Edit Plan Page Continue Button',
  PRESSED_CL_TRUTH_IN_LENDING_PAGE_PAYMENT_AUTHORISATION_LINK: 'Pressed CL Truth In Lending Page Payment authorisation link',
  PRESSED_CL_TRUTH_IN_LENDING_PAGE_TRUTH_IN_LENDING_DISCLOSURE_LINK: 'Pressed CL Truth In Lending Page Truth in lending disclosure link',
  PRESSED_CL_TRUTH_IN_LENDING_PAGE_LOAN_AGREEMENT_LINK: 'Pressed CL Truth In Lending Page Loan agreement link',
  PRESSED_CL_TRUTH_IN_LENDING_PAGE_CREDIT_SCORE_DISCLOSURE_LINK: 'Pressed CL Truth In Lending Page Credit score disclosure link',
  PRESSED_SUMMARY_PAGE_PAYMENT_AUTHORISATION_LINK: `Pressed ${PageNames.SUMMARY} Page Payment authorisation link`,
  PRESSED_SUMMARY_PAGE_TRUTH_IN_LENDING_DISCLOSURE_LINK: `Pressed ${PageNames.SUMMARY} Page Truth in lending disclosure link`,
  PRESSED_SUMMARY_PAGE_LOAN_AGREEMENT_LINK: `Pressed ${PageNames.SUMMARY} Page Loan agreement link`,
  PRESSED_SSN_TOOLTIP: 'Pressed SSN Tooltip',
  PRESSED_MANUAL_ADDRESS_TOGGLE: 'Pressed Manual Address toggle',
  PRESSED_PATRIOT_ACT_LINK: 'Pressed Patriot act link',
  PRESSED_CLOSE_CL_IDV_PAGE: 'Pressed Close CL IDV Page',

  CHANGED_FIRST_NAME: 'Changed First Name',
  CHANGED_MIDDLE_NAME: 'Changed Middle Name',
  CHANGED_LAST_NAME: 'Changed Last Name',
  CHANGED_DOB: 'Changed DOB',
  CHANGED_ADDRESS: 'Changed Address',
  SSN_FORM_FIELD_ENTERED: 'SSN Form Field Entered',

  VIEWED_CL_MARKETING_PAGE: 'Viewed CL Marketing Page',
  PRESSED_CL_MARKETING_PAGE_PAY_IN_4_BUTTON: 'Pressed CL Marketing Page Pay in 4 button',
  PRESSED_CL_MARKETING_PAGE_APPLY_NOW_BUTTON: 'Pressed CL Marketing Page Apply now button',
  PRESSED_CL_MARKETING_PAGE_COMMON_QUESTIONS: 'Pressed CL Marketing Page common questions',

  APPLE_PAYLATER_DEBUG: 'Apple Pay Later Debug',
  VIEWED_APPLE_PAYLATER_ENTRY_PAGE: 'Viewed Apple Pay Later Entry Page',
  PRESSED_APPLE_PAYLATER_CARD_EDUCATION_CONTINUE_BUTTON: 'Pressed Apple Pay Later Card Education Continue Button',
  VIEWED_CL_ENTRY_PAGE: 'Viewed CL Entry Page',
  PRESSED_CL_ENTRY_PAGE_COMPARISON_TOOLTIP: 'Pressed CL Entry Page Comparison Tooltip',
  PRESSED_CL_ENTRY_PAGE_DISCLAIMER_LINK: 'Pressed CL Entry Page Disclaimer Link',

  PRESSED_NON_TERMINAL_ERROR_PAGE_CONTINUE_WITH_PAY_IN_4_BUTTON: 'Pressed Non Terminal error page Continue with Pay in 4 button',
  VIEWED_ONFIDO_SCAN_LANDING: 'Viewed Onfido Scan - Landing',
  VIEWED_UNABLE_TO_VERIFY_ID: 'Viewed Unable To Verify ID',

  PRESSED_SOCIAL_BANNER: 'Pressed Social Banner',
  PRESSED_SOCIAL_BANNER_UK: 'Pressed Social Banner UK',
  VIEWED_ORDER_COMPLETE: 'Viewed Order Complete',

  STUCK_AFTER_CLOSE: 'Stuck at processing modal after close',

  // verify sms service related
  VIEWED_VERIFY_PAGE_FAIL_TOAST: 'Viewed Verify Page Fail Toast',
  VIEWED_VERIFY_PAGE_TIMEOUT_TOAST: 'Viewed Verify Page TimeOut Toast',
  VIEWED_SMS_VERIFYING_PAGE: 'Viewed SMS Verifying Page',

  SHOW_TEST_TARGETING: 'Show Test Targeting',

  NEW_PAYMENT_METHOD_UPDATE_TRACE_ID: 'New Payment Method Update Trace id',
  CLICK_POST_CHECKOUT_IMAGE: 'Click Post Checkout Image',

  ACCOUNT_LOOKUP_RESPONSE_EMPTY: 'Account Look Up Response Empty',
  TRIGGERED_SMS_VERIFY_TIME_DELAY: 'Triggered SMS Verify Time Delay',

  TRIGGER_IDENTITY_REDIRECT_SUMMARY_PAGE: 'Trigger Identity Redirect Summary Page',

  VIEWED_PAY_MONTHLY_ONLY_PAGE: 'Viewed Pay Monthly Only Page',

  PRESSED_UPLOAD_MY_ID_BUTTON_ON_CONFIRM_DETAILS_PAGE: 'Pressed Upload My ID Button on Confirm Details Page',
  PRESSED_UPDATE_DETAILS_BUTTON_ON_CONFIRM_DETAILS_PAGE: 'Pressed Update Details Button on Confirm Details Page',

  PRESSED_SUBMIT_BUTTON_ON_VERIFY_RETRY_PAGE: 'Pressed Submit Button on Verify Retry Page',
  VIEWED_SAME_PROFILE_INFO_WARNING_ON_VERIFY_RETRY_PAGE: 'Viewed Same Profile Info Warning on Verify Retry Page',
  PRESSED_UPLOAD_MY_ID_BUTTON_ON_IDENTITY_START_PAGE: 'Pressed Upload my ID Button on Identity Start Page',
  VIEWED_ONFIDO_VERIFICATION_ERROR_TOAST: 'Viewed Onfido Verification Error Toast',
  VIEWED_PERSONA_VERIFICATION_ERROR_TOAST: 'Viewed Persona Verification Error Toast',
  VIEWED_PERSONA_VERIFICATION_EVENT: 'Viewed Persona Verification Event',
  VIEWED_PERSONA_VERIFICATION_SUCCESS: 'Viewed Persona Verification Success',
  ONFIDO_CHECK_RESULT_UPDATED: 'Onfido Check Result Updated',
  IDENTITY_CHECK_STATUS_UPDATED: 'Identity Check Status Updated',

  PRESSED_CHANGE_COUNTRY_BUTTON_LOGIN_EMAIL_PAGE: `Pressed ${PageNames.LOGIN_EMAIL} Change Country Button`,
  PRESSED_CHANGE_COUNTRY_BUTTON_SIGNUP_PAGE: `Pressed ${PageNames.SIGN_UP} Change Country Button`,
  PRESSED_NOT_YOU_BUTTON: `Pressed Not You Button`,

  REDIRECT_CROSS_REGION: 'Redirect Cross Region',
  HAS_CROSS_REGION_USER_SIGN_IN_EMAIL: 'Has Cross Region User Sign In Email',

  VIEWED_APP_DOWNLOAD: `Viewed App Download on ${PageNames.TERMINAL_ERROR} Page`,

  VIEWED_ALIGN_PAYMENT_MESSAGE: 'Viewed Align Payments Message',
  PRESSED_ALIGN_PAYMENT_SAVE_BUTTON: 'Pressed Align Payments Save Button',
  ALIGN_PAYMENT_SAVE_SUCCESS: 'Align Payments Save Success',
  ALIGN_PAYMENT_SAVE_FAILURE: 'Align Payments Save Failure',

  PRESSED_SUMMARY_PAGE_ALIGN_PAYMENTS_TAB: 'Pressed Summary Page Align Payments Tab',
  PRESSED_SUMMARY_PAGE_AUTOPAY_INFO_ICON: 'Pressed Summary Page Autopay Info Icon',
  TOGGLED_SUMMARY_PAGE_AUTOPAY_TOGGLE: 'Toggled Summary Page Autopay Toggle',
  PRESSED_SUMMARY_PAGE_AUTOPAY: 'Pressed Summary Page Autopay',
  VIEWED_AUTOPAY_HALF_SHEET: 'Viewed Autopay Half Sheet',
  VIEWED_AUTOPAY_ON_PAGE: 'Viewed CL Autopay on Page',
  VIEWED_AUTOPAY_OFF_PAGE: 'Viewed CL Autopay off Page',
  PRESSED_SUMMARY_PAGE_CHANGE_AUTOPAY_BUTTON: 'Pressed Summary Page Change Autopay Button',
  PRESSED_SUMMARY_PAGE_AUTOPAY_CLOSE_BUTTON: 'Pressed Summary Page Autopay Close Button',
  PRESSED_AUTOPAY_ON_PAGE_CLOSE_BUTTON: 'Pressed Close on CL Autopay on Page',
  PRESSED_AUTOPAY_OFF_PAGE_CLOSE_BUTTON: 'Pressed Close on CL Autopay off Page',
  PRESSED_AUTOPAY_ON_PAGE_CONFIRM_BUTTON: 'Pressed Confirm on CL Autopay on Page',
  PRESSED_AUTOPAY_ON_PAGE_ENABLE_BUTTON: 'Pressed Enable on CL Autopay on Page',
  PRESSED_AUTOPAY_OFF_PAGE_DISABLE_BUTTON: 'Pressed Disable on CL Autopay off Page',

  CL_CREDIT_FREEZE_APPLICATION_DEADLINE_PASSED: 'CL Credit Freeze Application Deadline passed',

  // EFA
  EFA_VERIFICATION_SUCCESS: 'EFA Verification Success',

  PRESSED_SIGN_UP_PAGE_CONTINUE: `Pressed ${PageNames.SIGN_UP} Page Continue`,
  OTP_LOGIN_ROUTE_AND_TRIGGER: 'OTP Login Route And Trigger',

  CASH_AUTHENTICATION_RESULT: 'Cash Authentication Result',
  CASH_AUTHENTICATION_TIMEOUT: 'Cash Authentication Timeout',
  CASH_FORCE_NEW_REQUEST_FAILED: 'Cash Force New Request Failed',

  ACCOUNT_LOOKUP_ATTEMPTED: 'Account Look Up Attempted',
  VIEWED_CASH_AUTH_PAGE: `Viewed ${PageNames.CASH_AUTH} Page`,

  GET_CHECKOUT_RETURN_LOOKUP_DATA: 'Get Checkout Return Lookup Data',

  PRESSED_SUBMIT_BUTTON_ON_KYC_PAGE: 'Pressed Submit Button on Re-KYC Confirm Detail Page',
  PRESSED_EDIT_BUTTON_ON_KYC_PAGE: 'Pressed Edit Button on Re-KYC Confirm Detail Page',
  PRESSED_UPDATE_ADDRESS_BUTTON: 'Pressed Update Address Button on Re-KYC Update Address Page',
  VIEWED_KYC_FAILING_TOAST: 'Viewed Re-KYC Failing Toast',
  VIEWED_KYC_SUCCESS_TOAST: 'Viewed Re-KYC Success Toast',

  KASADA_BLOCK: 'Kasada Block',
  AXIOS_NETWORK_ERROR: 'Axios Network Error',

  VIEWED_INITIAL_LOADING_PAGE: 'Viewed Initial Loading Page',

  SET_UUID_LOCAL_STORAGE_FAILED: 'Set Uuid Local Storage Failed',

  PROCESSED_PREFERRED_PAYMENT_DAY_ELIGIBILITY: 'Processed Preferred Payment Day Eligibility',
  UNSUITABILITY_QUESTIONS_FORM_CHANGED: 'Unsuitability Questions Form Changed',
  VIEWED_INCOME_COVERS_EXPENSES_QUESTION: 'Viewed Income Covers Expenses Question',
  UNSUITABILITY_QUESTIONS_FORM_SUBMITTED: 'Unsuitability Questions Form Submitted',

  VIEWED_ACCOUNT_CREATED_TOAST: 'Viewed Account Created Toast',
  VIEWED_ID_VERIFIED_TOAST: 'Viewed ID Verified Toast',
  VIEWED_REASSURANCE_TOAST: 'Viewed Reassurance Toast',
  DISMISSED_REASSURANCE_TOAST: 'Dismissed Reassurance Toast',
  VIEWED_UNSUITABILITY_QUESTIONS_ERROR_TOAST: 'Viewed Unsuitability Questions Error Toast',
  VIEWED_CONTRACT_AGREEMENT_ERROR_TOAST: 'Viewed Contract Agreement Error Toast',
  SOFT_ID_ENABLED: 'Soft ID Enabled',

  PRESSED_CONTINUE_BUTTON_ON_EDUCATION_INTRO_PAGE: 'Pressed NUX Confirm Button',

  PRESSED_PROFILE_CREATED_PAGE_CONTINUE_BUTTON: `Pressed ${PageNames.PROFILE_CREATED} Page Continue Button`,

  VIEWED_EXPENSE_CALCULATOR: 'Viewed Expense Calculator',
  EXPENSE_CALCULATOR_GO_BACK: 'Expense Calculator Go Back',

  SUBMITTED_AFFORDABILITY_QUESTIONS: 'Submitted Affordability Questions',

  PRESSED_WELCOME_WITH_INITIAL_LIMIT_CONTINUE_BUTTON: `Pressed ${PageNames.WELCOME_WITH_INITIAL_LIMIT} Page Continue Button`,

  // Cash inline auth
  VIEWED_CASH_WEB_LOGIN_OTP_PAGE: 'Viewed Cash Web Login OTP Page',
  ENTERED_CASH_WEB_LOGIN_OTP: 'Entered Cash Web Login OTP',
  CASH_WEB_LOGIN_OTP_FAILED: 'Cash Web Login OTP Failed',
  PRESSED_CASH_WEB_LOGIN_RESEND_OTP_LINK: 'Pressed Cash Web Login Resend OTP link',
  VIEWED_CASH_WEB_LOGIN_PIN_PAGE: 'Viewed Cash Web Login PIN Page',
  ENTERED_CASH_WEB_LOGIN_PIN: 'Entered Cash Web Login PIN',
  CASH_WEB_LOGIN_PIN_FAILED: 'Cash Web Login PIN Failed',
  PRESSED_CASH_WEB_LOGIN_FORGOT_PIN_LINK: 'Pressed Cash Web Login Forgot PIN link',
  CASH_LOGIN_SUCCESS: 'Cash Login Success',
  CASH_IDENTIFY_ACCOUNT_SUCCEEDED: 'Cash Identify Account Succeeded',
  CASH_IDENTIFY_ACCOUNT_FAILED: 'Cash Identify Account Failed',
}
export type TrackingEvents = keyof typeof TrackingEvent
export type TrackingEventValue = (typeof TrackingEvent)[keyof typeof TrackingEvent]

export const PaymentMethodTypes: Record<PaymentMethodType, string> = {
  CREDIT_CARD: 'Card',
  ACH_BANK_ACCOUNT: 'BankAccount',
  APPLE_PAY: 'ApplePay',
  CASH_APP_PAY: 'CashPay',
  DEBT_COLLECTION_AGENCY: 'Debt collection agency',
  DISCOUNT: 'Discount',
  APPLE_PAY_LATER_APPLE_PAY: 'ApplePayLaterApplePay',
}

export enum NonTerminalErrors {
  ClCardExpiryError = 'clCardExpiryError',
  IdentityCheckoutFailureError = 'identityCheckoutFailureError',
  IdentityServiceFailureError = 'identityServiceFailureError',
}

export enum TOGGLE_STATES {
  EXPANDED = 'expanded',
  COLLAPSED = 'collapsed',
}

export interface AppleRetailTrackingProps {
  checkoutChannel?: 'ONLINE' | 'INSTORE' | 'TELESALES'
  loanItemIds?: string[]
  payinfullItemIds?: string[]
  isChargeatship?: boolean
  downPaymentAmount?: Money
  interestAmount?: Money
  tradeinAmount?: Money
  shippingFee?: Money
}
